import { useEffect, useRef } from 'react';

import { DocumentDetails_DocumentSummaryFragment } from '@/modules/documents/graphql/DocumentDetails.generated';

/**
 * Waits for a newly created summary to appear in the summaries list,
 * then transitions to it.
 *
 * Uses a baseline approach to avoid transitioning immediately:
 * 1. When shouldTransition is set to true, captures the current newest summary's timestamp as a baseline
 * 2. Waits for the summaries array to update with a truly newer summary (after mutation completes)
 * 3. Only transitions when a summary appears with createdAt > baseline
 *
 * This prevents the hook from transitioning to an existing summary before the new one arrives.
 */
export function useTransitionToNewestSummary({
  shouldTransition,
  summaries,
  onTransition,
}: {
  shouldTransition: boolean;
  summaries: DocumentDetails_DocumentSummaryFragment[];
  onTransition: (summaryId: string) => void;
}) {
  const baselineNewestAtRef = useRef<number | null>(null);

  useEffect(() => {
    if (!shouldTransition) {
      baselineNewestAtRef.current = null;
      return;
    }
    if (!summaries || summaries.length === 0) return;

    const newest = summaries.reduce((a, b) =>
      b.createdAt > a.createdAt ? b : a
    );

    // Initialize the baseline when we first start waiting
    if (baselineNewestAtRef.current == null) {
      baselineNewestAtRef.current = newest.createdAt.getTime();
      return;
    }

    // Transition only when a newer summary than the baseline appears
    if (newest.createdAt.getTime() > baselineNewestAtRef.current) {
      onTransition(newest.id);
      baselineNewestAtRef.current = null;
    }
  }, [shouldTransition, summaries, onTransition]);
}
